
export interface AppConfig {
    env: string;
    apiUrl: string;
    apiKey: string;
    appName: string;
    appVersion: string;
    tracewayConnectionString?: string;
    googleWebClientId: string;
    googleMobileClientId: string;
    googleTagId?: string;
    googleAnalyticsId?: string;
    miCode?: string;
    clarityProjectId: string;
    cfFilePath: string;
    cfVoiceBaseUrl?: string;
    logApiKey: string;
    logApiUrl: string;
    locationApiUrl: string;
    paymentReturnBaseUrl: string;
    appBaseUrl?: string;
    siteBaseUrl?: string;
    whatsappNumber: string;
    supportEmail: string;
    featurePremiumEnabled?: boolean;
}

declare global {
    interface Window {
        __APP_CONFIG__?: AppConfig;
    }
}

export default function getConfig() {
    if (typeof window !== "undefined" && window.__APP_CONFIG__) return window.__APP_CONFIG__;

    throw new Error(
        "App config missing: window.__APP_CONFIG__ was not found. Ensure the page layout injects it during prerender."
    );
}


function normalizeBaseUrl(value: string | undefined | null, fallback = "") {
    const trimmed = value?.trim() ?? "";
    if (!trimmed) {
        return fallback;
    }

    try {
        const url = new URL(trimmed);
        if (url.protocol !== "http:" && url.protocol !== "https:") {
            return fallback;
        }

        return url.toString().replace(/\/+$/, "");
    } catch {
        return fallback;
    }
}

function normalizeAppPath(path: string) {
    const trimmed = path.trim();
    if (!trimmed) {
        return "/";
    }

    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function normalizeHostname(value: string | undefined | null) {
    return value?.trim().toLowerCase().replace(/\.+$/, "") ?? "";
}

function isLocalHostname(hostname: string) {
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

export function resolveSiteBaseUrl(config: AppConfig | null | undefined) {
    return normalizeBaseUrl(config?.siteBaseUrl, "https://pteultra.com");
}

export function resolveAppBaseUrl(config: AppConfig | null | undefined) {
    return normalizeBaseUrl(
        config?.appBaseUrl || config?.paymentReturnBaseUrl,
        resolveSiteBaseUrl(config),
    );
}

export function buildAppUrl(path: string, config: AppConfig | null | undefined) {
    const baseUrl = resolveAppBaseUrl(config);
    const url = new URL(normalizeAppPath(path), `${baseUrl}/`);
    url.searchParams.set("ptu_source", "home");
    return url.toString();
}

export function getGoogleTagId(config: AppConfig | null | undefined) {
    return config?.googleTagId?.trim() || config?.googleAnalyticsId?.trim() || "";
}

export function isPremiumFeatureEnabled(config: AppConfig | null | undefined) {
    return config?.featurePremiumEnabled === true;
}

export function resolveCookieDomain(config: AppConfig | null | undefined, hostname?: string | null) {
    const normalizedHostname = normalizeHostname(hostname);

    if (normalizedHostname && isLocalHostname(normalizedHostname)) {
        return "";
    }

    const normalizedEnv = config?.env?.trim().toLowerCase() ?? "";
    if (normalizedEnv === "production") {
        return ".pteultra.com";
    }

    if (normalizedEnv === "development") {
        return ".dev.pteultra.com";
    }

    if (normalizedHostname === "dev.pteultra.com" || normalizedHostname.endsWith(".dev.pteultra.com")) {
        return ".dev.pteultra.com";
    }

    if (normalizedHostname === "pteultra.com" || normalizedHostname.endsWith(".pteultra.com")) {
        return ".pteultra.com";
    }

    return "";
}


export const appConfig: AppConfig | null = typeof window !== "undefined" && window.__APP_CONFIG__ ? window.__APP_CONFIG__ : null;
